
import DialogMixins from '@/mixins/DialogMixins';
import WidgetMixins from '@/mixins/WidgetMixins';
import { loadWidget, truncateContent } from '@/utils/helpers';
import Component, { mixins } from 'vue-class-component';
import { Getter, namespace } from 'vuex-class';
import moment from 'moment-timezone';
import { IAPIKeys, ISenderID, IUser } from '@/types/types';
import { Watch } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import { numeric, required, min, max } from 'vee-validate/dist/rules';
import axios from 'axios';
import { camelCase } from 'lodash';
import * as XLSX from 'xlsx';

const authModule = namespace('auth');
const apiKeyModule = namespace('apiKeys');
const smsModule = namespace('sms');
extend('required', {
  ...required,
  message: 'Field is required',
});
extend('numeric', {
  ...numeric,
  message: 'Invalid phone number provided',
});
extend('min', {
  ...min,
  message: `Invalid phone number provided`,
});
extend('max', {
  ...max,
  message: `Maximum phone number must be 13`,
});
extend('max_content', (value) => {
  if (value.length > 750) {
    return 'Message cannot have more than 750 characters';
  }
  return true;
});

@Component({
  name: 'PersonalizeSMS',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
    EmptyPage: loadWidget('pages/EmptyPage'),
    ProgressBar: loadWidget('widgets/CircularProgressLoader'),
    AddSMSSenderID: loadWidget(`widgets/AddSenderID`),
    ValidationProvider,
    ValidationObserver,
    SkeletonPreloader: loadWidget('widgets/SkeletonPreloader'),
    DeleteDialog: loadWidget('widgets/DeleteDialog'),
  },
  filters: {
    timestamp(value: string, format?: string) {
      return moment(value).format(format ?? 'Do MMMM, YYYY');
    },
    truncateContent,
  },
})
export default class PersonalizeSMS extends mixins(WidgetMixins, DialogMixins) {
  @Getter('getCreditBalance') creditBalance!: number;
  @Getter('getResetFormState') resetFormState!: boolean;
  @apiKeyModule.Getter('getAPIKeys') accessKeys!: IAPIKeys;
  @smsModule.Getter('getSenderIDList') smsIDList!: Array<
    ISenderID & { uuid: string; id: number; slug: string }
  >;
  @smsModule.Getter('getMessagePlaceholders') placeholders!: string[];
  @smsModule.Getter('getTemplateDownloadUrl') downloadUrl!: string;
  @smsModule.Getter('getSenderIDList') senderID!: ISenderID[];
  @authModule.Getter('getUserDetails') user!: IUser;

  content = '';
  contentCopy = '';
  currentCursorIndex = 0;
  /** wizard position: 1 compose, 2 upload & map, 3 review & send */
  step = 1;
  search = '';
  placeholder = 0;
  fileToUpload: File | null = null;
  senderId: string | null = null;

  /* bring-your-own template state */
  templateSource: 'system' | 'own' = 'system';
  ownFile: File | null = null;
  ownColumns: string[] = [];
  ownRows: Array<Record<string, unknown>> = [];
  columnMapping: {
    phone: string | null;
    tokens: Record<string, string | null>;
  } = { phone: null, tokens: {} };

  get filterPlaceholders(): string[] {
    return this.placeholders.filter(
      (placeholder) =>
        placeholder?.toLowerCase().indexOf(this.search?.toLowerCase()) > -1
    );
  }

  get messageTokens(): string[] {
    return [
      ...new Set(
        (this.content.match(/%(\w+)%/g) ?? []).map((token) =>
          token.replace(/%/g, '')
        )
      ),
    ];
  }

  /** phone + every message token that has a file column assigned */
  get mappedTokenCount(): number {
    const tokens = this.messageTokens.filter(
      (token) => !!this.columnMapping.tokens[token]
    ).length;
    return tokens + (this.columnMapping.phone ? 1 : 0);
  }

  get unmappedTokenCount(): number {
    return this.messageTokens.length + 1 - this.mappedTokenCount;
  }

  get ownRowCount(): number {
    const phoneColumn = this.columnMapping.phone;
    if (!phoneColumn) {
      return this.ownRows.length;
    }
    return this.ownRows.filter(
      (row) => `${row[phoneColumn] ?? ''}`.trim() !== ''
    ).length;
  }

  get ownPreview(): string {
    const phoneColumn = this.columnMapping.phone;
    if (!this.content.trim() || !phoneColumn || this.ownRows.length === 0) {
      return '';
    }
    const row = this.ownRows.find(
      (candidate) => `${candidate[phoneColumn] ?? ''}`.trim() !== ''
    );
    if (!row) {
      return '';
    }
    return this.renderRow(this.content, row);
  }

  /**
   * What the phone rail renders: the personalized message for the first
   * recipient once a file is mapped, otherwise the raw message as composed.
   */
  get livePreview(): string {
    return this.ownPreview || this.content || '';
  }

  get canSend(): boolean {
    if (!this.senderId) {
      return false;
    }
    if (this.templateSource === 'own') {
      return (
        !!this.ownFile &&
        this.ownRowCount > 0 &&
        !!this.columnMapping.phone &&
        this.content.trim() !== ''
      );
    }
    return !!this.fileToUpload;
  }

  /**
   * Gate for the wizard's primary button. Step 01 holds the whole campaign
   * setup (sender, file, message, mapping), so it must be fully valid before
   * review — there is nothing left to fix on step 02.
   */
  get canAdvance(): boolean {
    return !this.inProgress && this.canSend;
  }

  get summaryRecipients(): number {
    return this.templateSource === 'own' ? this.ownRowCount : 0;
  }

  get summarySegments(): number {
    return Math.ceil(this.livePreview.length / 160) || 0;
  }

  get summaryCredits(): number {
    return this.summaryRecipients * this.summarySegments;
  }

  /** wizard primary action: advance to review, then send */
  onWizardSubmit(): void {
    if (this.step < 2) {
      this.step += 1;
      return;
    }
    this.sendSMS();
  }

  compose(ev: Event): void {
    this.content = (ev.target as HTMLInputElement).value;
  }

  addToTextBox(text: string): void {
    const textAreaId = document.getElementById(
      'content'
    ) as HTMLTextAreaElement;
    const formatText = `%${text}%`;
    textAreaId.focus();
    textAreaId.setRangeText(
      formatText,
      textAreaId.selectionStart,
      textAreaId.selectionEnd,
      'end'
    );
    // setRangeText does not fire input events; sync the reactive copy
    this.content = textAreaId.value;
  }

  insertColumnToken(column: string): void {
    this.addToTextBox(camelCase(column) || 'field');
  }

  private normalizeKey(value: string): string {
    return value.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  autoMapColumns(): void {
    const tokens: Record<string, string | null> = {};
    for (const token of this.messageTokens) {
      const existing = this.columnMapping.tokens[token];
      tokens[token] =
        existing && this.ownColumns.includes(existing)
          ? existing
          : this.ownColumns.find(
              (column) => this.normalizeKey(column) === this.normalizeKey(token)
            ) ?? null;
    }
    const phone =
      this.columnMapping.phone &&
      this.ownColumns.includes(this.columnMapping.phone)
        ? this.columnMapping.phone
        : this.ownColumns.find((column) =>
            /phone|mobile|msisdn|contact|recipient|number/.test(
              this.normalizeKey(column)
            )
          ) ?? null;
    this.columnMapping = { phone, tokens };
  }

  private renderRow(template: string, row: Record<string, unknown>): string {
    return template.replace(/%(\w+)%/g, (token, key) => {
      const source = this.columnMapping.tokens[key];
      if (!source) {
        return token;
      }
      const value = row[source];
      return value === undefined || value === null || `${value}`.trim() === ''
        ? token
        : `${value}`;
    });
  }

  @Watch('ownFile')
  onOwnFileChange(file: File | null): void {
    if (!file) {
      this.ownColumns = [];
      this.ownRows = [];
      this.columnMapping = { phone: null, tokens: {} };
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const workbook = XLSX.read(reader.result, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const headerRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
          header: 1,
        });
        this.ownColumns = ((headerRows[0] as unknown[]) ?? [])
          .map((heading) => `${heading}`.trim())
          .filter((heading) => heading !== '');
        this.ownRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
          sheet,
          { defval: '' }
        );
        this.autoMapColumns();
        if (this.ownColumns.length === 0) {
          this.$store.dispatch(
            'snackBarMessage',
            'No column headings were found in the first row of that file',
            { root: true }
          );
          this.$store.dispatch('snackBarVisibility', true, { root: true });
        }
      } catch (e) {
        this.ownColumns = [];
        this.ownRows = [];
        this.$store.dispatch(
          'snackBarMessage',
          'We could not read that file. Upload a valid xlsx or csv file',
          { root: true }
        );
        this.$store.dispatch('snackBarVisibility', true, { root: true });
      }
    };
    reader.readAsArrayBuffer(file);
  }

  @Watch('content')
  onContentTokensChange(): void {
    if (this.ownColumns.length > 0) {
      this.autoMapColumns();
    }
  }

  @Watch('content')
  onContentChange(): void {
    if (this.downloadUrl) {
      this.$store.commit('sms/UPDATE_TEMPLATE_URL', null);
    }
  }

  @Watch('resetFormState')
  onFormChange(payload: boolean): void {
    if (payload) {
      this.$store.commit('sms/UPDATE_TEMPLATE_URL', null);
      this.content = '';
      this.fileToUpload = null;
      this.ownFile = null;
      /* send succeeded: walk the wizard back to the start */
      this.step = 1;
      this.columnMapping = { phone: null, tokens: {} };
      const contentBox = document.getElementById(
        'content'
      ) as HTMLTextAreaElement | null;
      if (contentBox) {
        contentBox.value = '';
      }
    }
    setTimeout(() => {
      this.$store.dispatch('resetFormValues', false, { root: true });
    }, 2000);
  }

  generateTemplate(): void {
    this.contentCopy = this.content;
    this.$store.dispatch('sms/generateCustomizeMessageTemplate', {
      message: this.content,
    });
  }

  downloadTemplate(): void {
    axios
      .get(this.downloadUrl, {
        responseType: 'blob',
      })
      .then((response) => {
        const blob = new Blob([response.data], {
            type: 'application/octet-stream',
          }),
          a = document.createElement('a');
        a.download = `${this.downloadUrl.split('/')?.pop()}`;
        a.href = URL.createObjectURL(blob);
        a.click();
        URL.revokeObjectURL(a.href);
        a.remove();
      })
      .catch((error) => {
        this.$store.dispatch(
          'snackBarMessage',
          error.response?.data?.message ??
            'An error occurred while trying to download',
          {
            root: true,
          }
        );
        this.$store.dispatch('snackBarVisibility', true, { root: true });
      });
  }

  sendSMS(): void {
    if (this.templateSource === 'own') {
      this.sendOwnTemplate();
      return;
    }
    let formData = new FormData();
    formData.append(
      'file',
      this.fileToUpload as File,
      (this.fileToUpload as File).name
    );
    this.$store.dispatch('sms/personalizeSMS', {
      body: formData,
      senderId: this.senderId,
    });
  }

  /**
   * Normalizes the user's own spreadsheet into the shape the backend template
   * pipeline expects (token columns + phone, with the message template on the
   * first row) and sends it through the existing personalize endpoint.
   */
  sendOwnTemplate(): void {
    const phoneColumn = this.columnMapping.phone;
    if (!phoneColumn || this.content.trim() === '') {
      this.$store.dispatch(
        'snackBarMessage',
        'Compose a message and choose which column holds the phone numbers',
        { root: true }
      );
      this.$store.dispatch('snackBarVisibility', true, { root: true });
      return;
    }
    const mappedTokens = this.messageTokens.filter(
      (token) => this.columnMapping.tokens[token]
    );
    const rows = this.ownRows
      .filter((row) => `${row[phoneColumn] ?? ''}`.trim() !== '')
      .map((row, index) => {
        const out: Record<string, unknown> = {};
        for (const token of mappedTokens) {
          out[token] = row[this.columnMapping.tokens[token] as string] ?? '';
        }
        out.phone = `${row[phoneColumn]}`.trim();
        out.message = index === 0 ? this.content : '';
        return out;
      });
    if (rows.length === 0) {
      this.$store.dispatch(
        'snackBarMessage',
        'No rows with a phone number were found in your file',
        { root: true }
      );
      this.$store.dispatch('snackBarVisibility', true, { root: true });
      return;
    }
    const sheet = XLSX.utils.json_to_sheet(rows, {
      header: [...mappedTokens, 'phone', 'message'],
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, 'template');
    const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    const file = new File([buffer], 'personalize-own-template.xlsx', {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const formData = new FormData();
    formData.append('file', file, file.name);
    this.$store.dispatch('sms/personalizeSMS', {
      body: formData,
      senderId: this.senderId,
    });
  }

  created(): void {
    this.$store.dispatch('auth/me');
    this.$store.dispatch('sms/listSenderID', { isApproved: true });
    this.$store.dispatch('sms/listPlaceholders');
    this.$store.dispatch('checkCreditBalance');
  }
}
